/**
 * The Charter's hostility writer (M5 spec §14.1, plan C14): rewrites M4's hostility matrix (`src/combat/hostility.ts`)
 * from the latest `RelationsView`, the local system's claim holder and the hold.
 *
 * - Outlaws as before: the raider team is hostile to every team but itself.
 * - Faction pairs at **skirmish** or **war** are mutually hostile.
 * - The player against a faction that wants them at **level 3** (everywhere), at **level 2** (in that faction's
 *   held systems: the local holder), or at a standing ≤ law.json `standing.hostileAt` (−50, everywhere).
 * - The guardian cell against the player only while the player carries tetherstone (spec §9.2; attacks on guardians
 *   make a grudge, which the matrix keeps).
 * - M6 (plan P22): the player corporation's skirmish and war pairs make that faction's team and the player's team
 *   (`PLAYER_CORP_TEAM_ID`: the pilot and the corporation ships fly as one team) mutually hostile.
 * - Player grudges (M4) stay: `setFromRelations` rewrites only the standing bit.
 *
 * Faction index f (strategic `FACTION` order) flies as team `factions.json teamId`. Allocation-free.
 */
import factionsJson from '../data/strategic/factions.json'
import lawJson from '../data/strategic/law.json'
import { PLAYER_CORP_TEAM_ID, PLAYER_TEAM_ID, RAIDER_TEAM_ID, TEAM_SLOTS, setFromRelations } from '../combat/hostility'
import { FACTION, FACTION_IDS, PLAYER_FACTION_ID, type CorpFactionId, type FactionId, type RelationsView } from '../strategic/types'

/** Faction index → local team id (−1 when the faction has none). */
export const FACTION_TEAM = new Int8Array(FACTION_IDS.length).fill(-1)
/** Team id → faction index (−1 for the player's team and unused teams). */
export const TEAM_FACTION = new Int8Array(TEAM_SLOTS).fill(-1)
/** Faction index → 1 for outlaws (raiders, the guardian cell). */
export const FACTION_OUTLAW = new Uint8Array(FACTION_IDS.length)

for (const f of factionsJson.factions) {
  const i = FACTION[f.id as FactionId] as number | undefined
  if (i === undefined) continue
  FACTION_TEAM[i] = f.teamId
  if (f.teamId >= 0 && f.teamId < TEAM_SLOTS) TEAM_FACTION[f.teamId] = i
  FACTION_OUTLAW[i] = f.outlaw ? 1 : 0
}

/** The guardian cell's team. */
export const GUARDIAN_TEAM_ID: number = FACTION_TEAM[FACTION.guardian]

export interface HostilityInputs {
  /** The latest relations (null before the first: only the outlaw defaults apply). */
  view: RelationsView | null
  /** Faction index holding the local system (−1 = unclaimed or unknown). */
  holder: number
  /** The player carries tetherstone. */
  tetherstone: boolean
  /** law.json `standing.hostileAt` (default from the data). */
  hostileAt?: number
}

function setPair(out: Uint8Array, a: number, b: number): void {
  if (a < 0 || b < 0 || a >= TEAM_SLOTS || b >= TEAM_SLOTS || a === b) return
  out[a * TEAM_SLOTS + b] = 1
  out[b * TEAM_SLOTS + a] = 1
}

/** The local team of a ladder-pair id: the player corporation flies as the player's team (P22). */
function pairTeam(id: CorpFactionId): number {
  return id === PLAYER_FACTION_ID ? PLAYER_CORP_TEAM_ID : FACTION_TEAM[FACTION[id]]
}

/** Fills `out` (`TEAM_SLOTS²` cells) with the standing hostility the inputs call for. Allocates nothing. */
export function buildStandingMatrix(out: Uint8Array, inp: HostilityInputs): void {
  out.fill(0)
  for (let t = 0; t < TEAM_SLOTS; t++) setPair(out, RAIDER_TEAM_ID, t)
  if (inp.tetherstone) setPair(out, GUARDIAN_TEAM_ID, PLAYER_TEAM_ID)
  const view = inp.view
  if (view === null) return
  const pairs = view.pairs
  for (let i = 0; i < pairs.length; i++) {
    const p = pairs[i]
    if (p.relation !== 'skirmish' && p.relation !== 'war') continue
    setPair(out, pairTeam(p.a), pairTeam(p.b))
  }
  const hostileAt = inp.hostileAt ?? lawJson.standing.hostileAt
  const { standings, wanted } = view.player
  for (let f = 0; f < FACTION_IDS.length; f++) {
    const id = FACTION_IDS[f]
    const level = wanted[id] ?? 0
    const standing = standings[id]
    const hostile = level >= 3 || (level >= 2 && inp.holder === f) || (standing !== undefined && standing <= hostileAt)
    if (hostile) setPair(out, FACTION_TEAM[f], PLAYER_TEAM_ID)
  }
}

const scratch = new Uint8Array(TEAM_SLOTS * TEAM_SLOTS)

/** Rewrites the live hostility matrix from the inputs (grudges kept). */
export function writeHostility(inp: HostilityInputs): void {
  buildStandingMatrix(scratch, inp)
  setFromRelations(scratch)
}
